/**
 * Lenavio Talent Map API client.
 *
 * Mirror of ``backend/app/routers/talent.py``. All types here are
 * the source of truth for every Talent Map page so the matrix
 * shape stays consistent across the radar / heatmap / gap views.
 */
import client from "./client";

export type SkillCategory =
  | "technical"
  | "soft"
  | "leadership"
  | "domain"
  | "certification";

export type ProficiencyLevel = 1 | 2 | 3 | 4;
export type SkillSource =
  | "self_reported"
  | "manager_assessed"
  | "ai_inferred";
export type PlanStatus = "active" | "completed" | "paused";

export interface Skill {
  id: string;
  org_id: string;
  name: string;
  category: SkillCategory;
  description: string | null;
  is_default_template: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  employee_count: number;
  avg_proficiency: number | null;
}

export interface SkillCreatePayload {
  name: string;
  category?: SkillCategory;
  description?: string;
}

export interface SkillUpdatePayload {
  name?: string;
  category?: SkillCategory;
  description?: string;
  is_active?: boolean;
}

export interface EmployeeSkill {
  id: string;
  employee_id: string;
  skill_id: string;
  proficiency_level: number;
  source: SkillSource;
  verified_by: string | null;
  notes: string | null;
  added_at: string;
  skill_name: string | null;
  skill_category: string | null;
}

export interface RoleSkillRequirement {
  skill_id: string;
  skill_name: string;
  skill_category: string;
  required_level: number;
  current_level: number;
  is_critical: boolean;
  gap: number;
}

export interface EmployeeSkillProfile {
  employee_id: string;
  employee_name: string;
  role_title: string | null;
  skills: EmployeeSkill[];
  requirements: RoleSkillRequirement[];
}

export interface EmployeeSkillsUpsertPayload {
  skills: {
    skill_id: string;
    proficiency_level: ProficiencyLevel;
    source?: SkillSource;
    notes?: string;
  }[];
  replace_all?: boolean;
}

export interface AIAssessSuggestion {
  skill_name: string;
  category: SkillCategory;
  proficiency_level: number;
  rationale: string;
}

export interface AIAssessResponse {
  suggestions: AIAssessSuggestion[];
  model: string;
}

export interface RoleGapSummary {
  role_title: string;
  employee_count: number;
  critical_gaps: number;
  total_gaps: number;
  sample_employees: string[];
  missing_critical_skills: string[];
}

export interface DepartmentGapSummary {
  department_id: string | null;
  department_name: string;
  employees: number;
  avg_proficiency: number | null;
  coverage: number;
  weakest_skills: string[];
}

export interface GapReport {
  roles: RoleGapSummary[];
  departments: DepartmentGapSummary[];
}

export interface HeatmapCell {
  department_id: string | null;
  department_name: string;
  skill_id: string;
  skill_name: string;
  skill_category: string;
  avg_proficiency: number;
  holder_count: number;
}

export interface HeatmapResponse {
  cells: HeatmapCell[];
  departments: string[];
  skills: string[];
}

export interface DevelopmentPlan {
  id: string;
  org_id: string;
  employee_id: string;
  skill_id: string;
  target_level: number;
  current_level: number;
  target_date: string | null;
  status: PlanStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  skill_name: string | null;
}

export interface DevelopmentPlanCreatePayload {
  employee_id: string;
  skill_id: string;
  target_level: ProficiencyLevel;
  target_date?: string;
  notes?: string;
}

export interface DevelopmentPlanUpdatePayload {
  target_level?: ProficiencyLevel;
  target_date?: string;
  status?: PlanStatus;
  notes?: string;
}

// ── Endpoints ──

export async function listSkills(opts?: {
  include_inactive?: boolean;
  category?: SkillCategory;
}): Promise<Skill[]> {
  const res = await client.get<Skill[]>("/skills", { params: opts });
  return res.data;
}

export async function createSkill(data: SkillCreatePayload): Promise<Skill> {
  const res = await client.post<Skill>("/skills", data);
  return res.data;
}

export async function updateSkill(
  id: string,
  data: SkillUpdatePayload,
): Promise<Skill> {
  const res = await client.patch<Skill>(`/skills/${id}`, data);
  return res.data;
}

export async function deleteSkill(id: string): Promise<void> {
  await client.delete(`/skills/${id}`);
}

export async function getEmployeeProfile(
  employeeId: string,
): Promise<EmployeeSkillProfile> {
  const res = await client.get<EmployeeSkillProfile>(
    `/skills/employees/${employeeId}`,
  );
  return res.data;
}

export async function upsertEmployeeSkills(
  employeeId: string,
  payload: EmployeeSkillsUpsertPayload,
): Promise<EmployeeSkill[]> {
  const res = await client.put<EmployeeSkill[]>(
    `/skills/employees/${employeeId}`,
    payload,
  );
  return res.data;
}

export async function aiAssessEmployee(
  employeeId: string,
  extraContext?: string,
): Promise<AIAssessResponse> {
  const res = await client.post<AIAssessResponse>("/skills/assess", {
    employee_id: employeeId,
    extra_context: extraContext,
  });
  return res.data;
}

export async function upsertRoleRequirement(payload: {
  role_title: string;
  skill_id: string;
  required_level: ProficiencyLevel;
  is_critical?: boolean;
}): Promise<{ status: string }> {
  const res = await client.post("/skills/requirements", payload);
  return res.data;
}

export async function getGapReport(): Promise<GapReport> {
  const res = await client.get<GapReport>("/skills/gaps");
  return res.data;
}

export async function getHeatmap(): Promise<HeatmapResponse> {
  const res = await client.get<HeatmapResponse>("/skills/map");
  return res.data;
}

export async function createDevelopmentPlan(
  payload: DevelopmentPlanCreatePayload,
): Promise<DevelopmentPlan> {
  const res = await client.post<DevelopmentPlan>(
    "/skills/development-plan",
    payload,
  );
  return res.data;
}

export async function listEmployeePlans(
  employeeId: string,
): Promise<DevelopmentPlan[]> {
  const res = await client.get<DevelopmentPlan[]>(
    `/skills/development-plan/employees/${employeeId}`,
  );
  return res.data;
}

export async function updateDevelopmentPlan(
  id: string,
  payload: DevelopmentPlanUpdatePayload,
): Promise<DevelopmentPlan> {
  const res = await client.patch<DevelopmentPlan>(
    `/skills/development-plan/${id}`,
    payload,
  );
  return res.data;
}
